import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { PRODUCT_V3_DETAIL_KEY, STOCKS_V3_KEY } from 'common/constants/queryKeys'
import { useMutateCreateStock, useMutateUpdateStock } from 'modules/inventory/api/mutation'
import { useStockById, useWarehouse } from 'modules/inventory/api/queries'
import type { ProductV3Detail } from 'modules/inventory/api/type'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import * as Yup from 'yup'

import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { formatWithCommasPreserveDecimals } from 'utils/formatNumber'

interface AddStockDialogProps {
  open: boolean
  onClose: () => void
  product: ProductV3Detail
  stockId?: string
}

interface StockForm {
  lot_number: string
  quantity: string
  warehouse_id: string
  price: string
  price_per_unit: string
}

const AddStockDialog = ({ open, onClose, product, stockId }: AddStockDialogProps) => {
  const { t } = useTranslation(['stock', 'product'])
  const qc = useQueryClient()
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const isEdit = Boolean(stockId)

  const { data: existingStock } = useStockById(stockId)

  const schema = Yup.object().shape({
    lot_number: Yup.string().optional(),
    quantity: Yup.string()
      .required(t('stock:please.input.quantity'))
      .test('gt-zero', 'ต้องมากกว่า 0', (v) => Number(String(v).replace(/,/g, '')) > 0),
    warehouse_id: Yup.string().required(t('stock:please.input.warehouse')),
    price: Yup.string().required(t('stock:please.input.price')),
    price_per_unit: Yup.string().required(t('stock:please.input.price')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<StockForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { lot_number: '', quantity: '', warehouse_id: '', price: '', price_per_unit: '' },
    resolver,
  })

  // Pre-fill form when editing
  useEffect(() => {
    if (open && isEdit && existingStock) {
      reset({
        lot_number: existingStock.lot_number ?? '',
        quantity: String(existingStock.quantity),
        warehouse_id: String(existingStock.warehouse.id),
        price: formatWithCommasPreserveDecimals(existingStock.price),
        price_per_unit: formatWithCommasPreserveDecimals(existingStock.price_per_unit),
      })
    }
  }, [open, isEdit, existingStock, reset])

  const {
    data: warehouseData,
    isLoading: isWarehouseLoading,
    isError: isWarehouseError,
    hasNextPage: hasNextWarehouse,
    fetchNextPage: fetchNextWarehouse,
    isFetchingNextPage: isFetchingNextWarehouse,
  } = useWarehouse()

  const handleWarehouseScroll = useInfiniteScrollTable({
    isLoading: isWarehouseLoading || isFetchingNextWarehouse,
    onLoading: () => { if (hasNextWarehouse) fetchNextWarehouse() },
  })

  const warehouses =
    warehouseData?.pages.flatMap((p) => p.warehouses).filter((w) => w?.is_active !== false) ?? []

  const createStockMutation = useMutateCreateStock()
  const updateStockMutation = useMutateUpdateStock()

  const calcPricePerUnit = (quantityRaw: string, priceRaw: string) => {
    const qty = Number(quantityRaw.replace(/,/g, ''))
    const price = Number(priceRaw.replace(/,/g, ''))
    if (qty > 0 && price > 0) {
      setValue('price_per_unit', formatWithCommasPreserveDecimals(Number((price / qty).toFixed(2))))
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data: StockForm) => {
    setIsSubmitLoading(true)
    try {
      if (isEdit && stockId) {
        await updateStockMutation.mutateAsync({
          body: {
            lot_number: data.lot_number || null,
            quantity: Number(data.quantity.replace(/,/g, '')),
            price: Number(data.price.replace(/,/g, '')),
            price_per_unit: Number(data.price_per_unit.replace(/,/g, '')),
          },
          stockId,
        })
      } else {
        await createStockMutation.mutateAsync({
          product_id: product.product_id,
          lot_number: data.lot_number || null,
          quantity: Number(data.quantity.replace(/,/g, '')),
          warehouse_id: data.warehouse_id,
          price: Number(data.price.replace(/,/g, '')),
          price_per_unit: Number(data.price_per_unit.replace(/,/g, '')),
        })
      }
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      handleClose()
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const warehouseName =
    isEdit && existingStock
      ? warehouses.find((w) => String(w.id) === String(existingStock.warehouse.id))?.name ??
        existingStock.warehouse.name
      : undefined

  return (
    <CustomDialog
      open={open}
      closeDialog={handleClose}
      title={isEdit ? t('product:inventory.edit.lot') : t('product:inventory.add_lot')}
      submitLabel={t('stock:stock.save')}
      formName="add-stock-form"
      isSubmitLoading={isSubmitLoading}
      onSubmit={() => {}}
      enableCloseButton
      enableInLineWithTitle
      maxWidth="sm"
      fullWidth
      contentMinWidth={0}
    >
      <form id="add-stock-form" onSubmit={handleSubmit(onSubmit)}>
        <Card
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1,
            mb: 2,
            bgcolor: '#F5F5F5',
            borderColor: '#EAEAEA',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={product.product_img_url ?? undefined}
              alt={product.product_name}
              variant="rounded"
              sx={{ width: 44, height: 44, borderRadius: 1.5 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {product.product_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('stock:stock.unit')} | {product.unit_abbreviation}
              </Typography>
            </Box>
          </Stack>
        </Card>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            {t('stock:stock.lot.label')}
          </label>
          <Controller
            name="lot_number"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                placeholder={t('stock:placeholder.lot')}
                error={!!errors.lot_number}
                helperText={errors.lot_number?.message}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('stock:stock.quantity')}
          </label>
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                placeholder={t('stock:placeholder.quantity')}
                error={!!errors.quantity}
                helperText={errors.quantity?.message}
                onChange={(e) => {
                  field.onChange(e)
                  calcPricePerUnit(e.target.value, getValues('price'))
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{product.unit_abbreviation}</InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('stock:stock.warehouse')}
          </label>
          <Controller
            name="warehouse_id"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                disabled={isWarehouseLoading || isWarehouseError || isEdit}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (isEdit) return warehouseName ?? ''
                    if (!selected)
                      return (
                        <span style={{ opacity: 0.6 }}>{t('stock:please.input.warehouse')}</span>
                      )
                    return warehouses.find((w) => String(w.id) === String(selected))?.name ?? ''
                  },
                  MenuProps: {
                    PaperProps: {
                      onScroll: handleWarehouseScroll,
                      sx: { maxHeight: 200, overflowY: 'auto' },
                    },
                  },
                }}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
                {isFetchingNextWarehouse && (
                  <MenuItem disabled sx={{ justifyContent: 'center' }}>
                    <CircularProgress size={20} />
                  </MenuItem>
                )}
              </TextField>
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('stock:stock.lot.price.label')}
          </label>
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="0.00"
                error={!!errors.price}
                helperText={errors.price?.message}
                onChange={(e) => {
                  field.onChange(e)
                  calcPricePerUnit(getValues('quantity'), e.target.value)
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('stock:bath')}</InputAdornment>,
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('stock:stock.price.per.unit')} ({product.unit_abbreviation})
          </label>
          <Controller
            name="price_per_unit"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="0.00"
                error={!!errors.price_per_unit}
                helperText={errors.price_per_unit?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('stock:bath')}</InputAdornment>,
                }}
              />
            )}
          />
        </Box>
      </form>
    </CustomDialog>
  )
}

export default AddStockDialog
